import { TooltipWrapper } from '@components/TooltipWrapper';
import { Checkbox } from '@components/checkbox';
import { cn } from '@utils/helpers';
import { useFragment } from 'react-relay';
import { OtherDocuments } from '../otherDocuments';
import { aboHeaderFragment } from './AboHeader.relay';
import { SortIcon, SubscribedCounter } from './lib';
import type { IAboTableHeaderProps, IAboTableHeaderRelayProps } from './types';

const AboTableHeader: React.FC<IAboTableHeaderProps> = (props: IAboTableHeaderProps) => {
  const header = useFragment(aboHeaderFragment, props.header);

  const getColDocState = (col: string) => {
    if (col === 'otherall') {
      const otherAllDocs = getDocs(col);

      const otherAllDocsHeaderState = otherAllDocs.map((col) => {
        const sliced = Object.values(props.checkState).map((docs) => Number(docs[col]));

        return sliced.every((doc) => doc === 1) ? 1 : sliced.indexOf(1) > -1 || sliced.indexOf(2) > -1 ? 2 : 0;
      });

      return otherAllDocsHeaderState.every((doc) => doc === 1) ? 1 : otherAllDocsHeaderState.indexOf(1) > -1 || otherAllDocsHeaderState.indexOf(2) > -1 ? 2 : 0;
    }

    const allColSliced = Object.values(props.checkState).map((docs) => Number(docs[col]));
    return allColSliced.every((doc) => doc === 1) ? 1 : allColSliced.indexOf(1) > -1 || allColSliced.indexOf(2) > -1 ? 2 : 0;
  };

  const getDocs = (col: string) => {
    if (col === 'otherall') {
      const docLabelKeys = props.documentLabels.labels.map((label) => label.key);
      return docLabelKeys.filter((key) => props.excludedColsInOtherDocs.indexOf(key) < 0);
    }
    return [col];
  };

  const trancheIds = Object.keys(props.checkState);

  return (
    <thead>
      <tr>
        {header.tableheader.map((headerItem, idx) => {
          const docs = getDocs(headerItem.col);
          const colDocState = getColDocState(headerItem.col);
          const selectedOtherAllDocs = {};
          if (headerItem.col === 'otherall') {
            for (const doc of docs) {
              selectedOtherAllDocs[doc] = getColDocState(doc);
            }
          }

          return (
            <th
              key={headerItem.label}
              className={cn(
                `AboTable-Header-cell-${headerItem.col} [&_.Checkbox-with-label_.Checkbox-label]:whitespace-pre-wrap font-normal font-heading text-xl`,
                ['all', 'kid', 'factsheet', 'otherall'].includes(headerItem.col) ? 'text-xs! text-black font-body! not-italic!' : '',
                ['other', 'otherall'].includes(headerItem.col) && 'w-[200px] max-[480px]:w-[25px]',
                ['all', 'kid', 'factsheet', 'isin'].includes(headerItem.col) && 'max-[1100px]:hidden',
                headerItem.col === 'otherall' && '[&_.OtherDocuments>.Checkbox-with-label]:my-[3px] max-[480px]:[&>span>span]:hidden',
              )}
            >
              <span className="flex flex-nowrap justify-start items-center">
                {props.excludedColsInOtherDocs.indexOf(headerItem.col) > -1 ? (
                  <Checkbox
                    checked={colDocState === 1}
                    indeterminate={colDocState === 2}
                    updateCallback={(newVal) => {
                      props.checkFn(trancheIds, docs, newVal);
                    }}
                  />
                ) : null}
                {headerItem.col !== 'otherall' ? (
                  <button type="button" onClick={() => props.sortHandler(headerItem.col)} className="flex items-end-safe bg-transparent border-none p-0 cursor-pointer font-inherit text-inherit">
                    <span className="flex items-center gap-1">
                      {headerItem.col === 'kid' ? <TooltipWrapper text="Kundeninformationsdokument">{headerItem.label}</TooltipWrapper> : headerItem.label}
                      {['all', 'kid', 'factsheet'].indexOf(headerItem.col) > -1 ? null : <SortIcon sortDirection={props.sortInfo[headerItem.col] || 0} />}
                    </span>
                    {headerItem.col === 'fundsname' ? (
                      <SubscribedCounter count={Object.values(props.checkState).reduce<number>((prev: number, curr: any) => (Object.values(curr).indexOf(true) > -1 ? prev + 1 : prev), 0)} />
                    ) : null}
                  </button>
                ) : null}
                {headerItem.col === 'otherall' ? (
                  <OtherDocuments
                    id={trancheIds}
                    checkFn={props.checkFn}
                    otherDocuments={{ other: selectedOtherAllDocs, otherall: colDocState }}
                    documentLabels={props.documentLabels as any}
                    excludedColsInOtherDocs={props.excludedColsInOtherDocs}
                    fixedLabel={props.documentLabels.documentLabelsTitle}
                  />
                ) : null}
              </span>
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

export { AboTableHeader };
export type { IAboTableHeaderRelayProps };
